/**
 * Phase D — DealAlert KI-Berater chat panel.
 *
 * Mounted inside `ListingDetailPage` after the original description
 * block. Single-listing chat; conversation is auto-keyed by
 * `(user, listing)` on the backend. Auth required (handled by the
 * parent route).
 *
 * Behaviour:
 *   - On mount: GET /api/ai/conversations/by-listing/{ulid} — if it
 *     returns 200 we seed `messages` from `doc.turns`. If 404 we keep
 *     empty state. Never auto-triggers a Claude call.
 *   - Tap quick-action chip → POST /api/ai/deal-advisor with the slug.
 *   - Type in composer → POST /api/ai/deal-advisor with `message`.
 *   - 429: bubble the German message + Retry-After countdown.
 *   - "Neue Konversation": confirms, sets the next send to also
 *     include `reset_conversation: true`.
 *
 * Visual:
 *   - Section header with sparkle icon + "Powered by Claude" tagline.
 *   - Quick-action chips wrap on mobile, single row scrollable on
 *     wider screens.
 *   - Alternating bubbles: user right (zinc-700), assistant left
 *     (emerald-tinted). Whitespace-pre-wrap so Claude's section
 *     breaks render. Lightweight **bold** + *italic* + headings.
 *   - Per-assistant-message footer: model · in/out tokens · ~$X.XXX
 *   - Composer textarea auto-resizes 1-4 rows, Enter sends, Shift+Enter
 *     newlines. Disabled while a turn is in flight.
 *
 * DE-only copy held in `COPY` at top of file so an EN locale can be
 * dropped in later without spelunking through JSX.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { formatApiError } from "../api/client";
import {
  fetchAdminUsageSummary,
  fetchConversationByListing,
  fetchQuickActions,
  sendAdvisorTurn,
  streamAdvisorTurn,
} from "../api/aiApi";
import { useAuth } from "../auth/AuthContext";
import { PaywallModal, ProUpgradeButton } from "./billing/PaywallComponents";
import { useSpeechRecognition } from "../hooks/useSpeechRecognition";
import { Mic, MicOff, X } from "lucide-react";
import { ShareDealButton } from "./ShareDealButton";
import { ChatWidgets } from "./ai/ChatWidgets";
import {
  registerAdvisorSnapshotter,
} from "../utils/advisorScrollSnapshot";

/* ---------------- Copy (i18n — Welle 3) ----------------
 * Getter-based so every existing `COPY.x` call site resolves through
 * i18next at render time (de/en/hr via the `ai` namespace). */
import i18n from "../i18n";

const _t = (k, o) => i18n.t(`ai:${k}`, o);
const COPY = {
  get sectionTitle() { return _t("sectionTitle"); },
  get poweredBy() { return _t("poweredBy"); },
  get emptyHeadline() { return _t("emptyHeadline"); },
  get emptySubline() { return _t("emptySubline"); },
  get composerPlaceholder() { return _t("composerPlaceholder"); },
  get sendBtn() { return _t("sendBtn"); },
  get thinking() { return _t("thinking"); },
  get resetLink() { return _t("resetLink"); },
  get resetConfirm() { return _t("resetConfirm"); },
  get rateLimitedPrefix() { return _t("rateLimitedPrefix"); },
  retryIn: (s) =>
    s > 60
      ? _t("retryInMin", { min: Math.ceil(s / 60) })
      : _t("retryInSec", { sec: Math.max(s, 1) }),
  get errorRetry() { return _t("errorRetry"); },
  get genericError() { return _t("genericError"); },
  get loadingChips() { return _t("loadingChips"); },
  get adminSessionLabel() { return _t("adminSessionLabel"); },
  get adminWeeklyLabel() { return _t("adminWeeklyLabel"); },
};

/** Quick-action chip label — backend ships slug + German fallback. */
const qaLabel = (item) =>
  _t(`quickActions.${item?.slug}`, { defaultValue: item?.label || "" });

/* ---------------- Tiny inline markdown renderer ---------------- */
/**
 * Renders a string with Claude's typical Markdown subset: `**bold**`,
 * `*italic*`, leading `# `/`## `/`### ` headings, and line breaks.
 * No external dep. Skip-listed for code fences / tables to keep the
 * implementation tiny.
 */
function MarkdownLite({ text }) {
  if (!text) return null;
  const lines = text.split(/\r?\n/);
  return (
    <>
      {lines.map((rawLine, lineIdx) => {
        const line = rawLine;
        let cls = "block";
        let inner = line;
        // Headings.
        const h3 = /^###\s+(.*)$/.exec(line);
        const h2 = /^##\s+(.*)$/.exec(line);
        const h1 = /^#\s+(.*)$/.exec(line);
        if (h1) {
          cls = "block text-zinc-100 font-semibold text-base mt-3 mb-1";
          inner = h1[1];
        } else if (h2) {
          cls = "block text-zinc-100 font-semibold text-sm mt-3 mb-1";
          inner = h2[1];
        } else if (h3) {
          cls =
            "block text-emerald-200 font-medium text-sm mt-2 mb-0.5 uppercase tracking-wide";
          inner = h3[1];
        }
        // Inline **bold** / *italic*. Order matters: bold first, then italic.
        const parts = [];
        let buf = inner;
        let key = 0;
        // **bold**
        while (true) {
          const m = /\*\*([^*]+)\*\*/.exec(buf);
          if (!m) break;
          parts.push(buf.slice(0, m.index));
          parts.push(
            <strong key={`b-${lineIdx}-${key++}`} className="text-zinc-50">
              {m[1]}
            </strong>,
          );
          buf = buf.slice(m.index + m[0].length);
        }
        parts.push(buf);
        // *italic* — apply to string parts only.
        const italicized = parts.map((part, i) => {
          if (typeof part !== "string") return part;
          const out = [];
          let rest = part;
          while (true) {
            const m = /(?<!\*)\*([^*\n]+)\*(?!\*)/.exec(rest);
            if (!m) break;
            out.push(rest.slice(0, m.index));
            out.push(
              <em
                key={`i-${lineIdx}-${i}-${out.length}`}
                className="text-zinc-200"
              >
                {m[1]}
              </em>,
            );
            rest = rest.slice(m.index + m[0].length);
          }
          out.push(rest);
          return <React.Fragment key={`p-${lineIdx}-${i}`}>{out}</React.Fragment>;
        });
        // Empty line → spacer.
        if (line.trim() === "") {
          return <span key={lineIdx} className="block h-2" />;
        }
        return (
          <span key={lineIdx} className={cls}>
            {italicized}
          </span>
        );
      })}
    </>
  );
}

/* ---------------- Helpers ---------------- */
function deAbbrev(n) {
  // 3520 → "3.520" (de-AT)
  if (n == null) return "0";
  return Number(n).toLocaleString("de-AT");
}

function fmtCostUsd(usd) {
  if (usd == null) return "$0";
  if (usd < 0.001) return `~$${usd.toFixed(5)}`;
  if (usd < 0.01) return `~$${usd.toFixed(4)}`;
  return `~$${usd.toFixed(3)}`;
}

function UsageFooter({ usage }) {
  if (!usage) return null;
  return (
    <div
      data-testid="ai-usage-footer"
      className="mt-1.5 text-[10px] text-zinc-500/80 tabular-nums"
    >
      {usage.model} · {deAbbrev(usage.input_tokens)} in /{" "}
      {deAbbrev(usage.output_tokens)} out ·{" "}
      {fmtCostUsd(usage.estimated_cost_usd)}
    </div>
  );
}

function ThinkingBubble() {
  return (
    <div
      data-testid="ai-thinking-bubble"
      className="flex items-start gap-2 mt-2"
    >
      <div className="px-4 py-3 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-zinc-300 text-sm flex items-center gap-2">
        <span className="inline-flex gap-1">
          <span
            className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"
            style={{ animationDelay: "0ms" }}
          />
          <span
            className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"
            style={{ animationDelay: "150ms" }}
          />
          <span
            className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"
            style={{ animationDelay: "300ms" }}
          />
        </span>
        <span>{COPY.thinking}</span>
      </div>
    </div>
  );
}

function MessageBubble({ turn }) {
  const isUser = turn.role === "user";
  if (isUser) {
    return (
      <div className="flex min-w-0 justify-end" data-testid="ai-turn-user">
        <div className="ai-bubble max-w-[80%] min-w-0 px-4 py-2.5 rounded-2xl rounded-br-md bg-zinc-700 text-zinc-100 text-sm whitespace-pre-wrap break-words leading-relaxed">
          {turn.content}
        </div>
      </div>
    );
  }
  return (
    <div className="flex min-w-0 flex-col items-start" data-testid="ai-turn-assistant">
      <div className="ai-bubble max-w-[92%] min-w-0 px-4 py-3 rounded-2xl rounded-bl-md bg-emerald-500/10 border border-emerald-500/20 text-zinc-200 text-sm break-words leading-relaxed">
        <MarkdownLite text={turn.content} />
      </div>
      <UsageFooter usage={turn.usage} />
    </div>
  );
}

function QuickActionChips({ items, disabled, onPick, isLoading }) {
  if (isLoading) {
    return (
      <div className="text-xs text-zinc-500">{COPY.loadingChips}</div>
    );
  }
  if (!items || items.length === 0) return null;
  return (
    <div
      data-testid="ai-quick-actions"
      className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full"
    >
      {items.map((it) => (
        <button
          key={it.slug}
          type="button"
          data-testid={`ai-quick-action-${it.slug}`}
          disabled={disabled}
          onClick={() => onPick(it.slug)}
          className="w-full text-left px-3 py-2 rounded-xl bg-zinc-900/80 border border-zinc-700 text-xs text-zinc-200 hover:border-emerald-400/60 hover:bg-zinc-900 hover:text-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {qaLabel(it)}
        </button>
      ))}
    </div>
  );
}

/**
 * P1.1 — Deal-specific opening insight (web).
 *
 * Rendered on the empty state ONLY. Uses ALREADY-COMPUTED backend
 * data (market signal, %-off, Rendite chips, comparable count, AI
 * insight one-liner, risk flags). Never issues an LLM call.
 */
function DealPreviewInsight({ card, vertical }) {
  if (!card) return null;
  const bullets = [];
  const sig = card.market_signal;
  const pct = card.price_vs_market_pct;
  const est = card.estimated_market_price;
  if (sig === "under_market" && pct != null) {
    bullets.push(_t("preview.underMarket", { pct: Math.abs(Math.round(pct)) }));
  } else if (sig === "over_market" && pct != null) {
    bullets.push(_t("preview.overMarket", { pct: Math.abs(Math.round(pct)) }));
  } else if (sig === "fair") {
    bullets.push(_t("preview.fair"));
  } else if (est != null) {
    bullets.push(
      _t("preview.estRef", { est: Math.round(est).toLocaleString("de-AT") }),
    );
  }
  if (vertical !== "auto" && card.rent_estimate && card.transaction_type !== "rent") {
    const re = card.rent_estimate;
    const lo = re.gross_yield_low;
    const hi = re.gross_yield_high;
    if (typeof lo === "number" && typeof hi === "number" && Number.isFinite(lo) && Number.isFinite(hi) && lo > 0) {
      bullets.push(_t("preview.yield", {
        low: lo.toFixed(1).replace(".", ","),
        high: hi.toFixed(1).replace(".", ","),
      }));
    }
  }
  if (vertical === "auto") {
    const facts = [];
    if (card.year) facts.push(String(card.year));
    if (card.mileage_km != null) facts.push(`${Math.round(card.mileage_km / 1000)}tkm`);
    if (card.fuel) facts.push(i18n.t(`enums:fuel.${card.fuel}`, { defaultValue: String(card.fuel) }));
    if (card.power_ps) facts.push(`${card.power_ps} PS`);
    if (facts.length) bullets.push(facts.slice(0, 4).join(" · "));
  }
  if (typeof card.comparable_count === "number" && card.comparable_count > 0) {
    bullets.push(_t("preview.comparables", { count: card.comparable_count }));
  }
  const oneLiner = (card.ai_insight_one_liner || card.ai_insight_de || card.ai_insight || "").trim();
  if (oneLiner) bullets.push(oneLiner);
  const risks = Array.isArray(card.risk_flags) ? card.risk_flags : [];
  if (risks.length) {
    bullets.push(_t("preview.risksCount", { count: risks.length }));
  }
  if (bullets.length === 0) return null;
  return (
    <div
      data-testid="ai-preview-insight"
      className="rounded-2xl border border-emerald-500/25 bg-emerald-500/[0.06] px-4 py-3 space-y-1.5 mb-4"
    >
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-300">
        <span aria-hidden>✦</span>
        <span>{_t("preview.headline")}</span>
      </div>
      {bullets.slice(0, 5).map((line, i) => (
        <div key={i} className="flex gap-2 text-sm text-zinc-100">
          <span className="text-emerald-300">•</span>
          <span className="min-w-0 flex-1">{line}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Phase UX Final Polish — P5. Renders the inline `suggested_followups`
 * extracted from Claude's reply as a vertical chip stack BELOW the
 * assistant bubble. Clicking a chip sends it as the next user turn.
 * Empty array hides the section entirely.
 */
function FollowupChips({ followups, disabled, onPick, turnIndex }) {
  if (!Array.isArray(followups) || followups.length === 0) return null;
  return (
    <div
      data-testid={`ai-followup-chips-${turnIndex}`}
      className="mt-2 w-full animate-in fade-in slide-in-from-bottom-1 duration-300"
    >
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 ml-1 mb-1.5">
        Weiter fragen:
      </div>
      {/* Mobile: single horizontally-scrollable row of compact chips
          (native scrollbar hidden). ≥sm: chips wrap onto multiple lines
          so desktop users see them all at a glance. */}
      <div
        className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1
                   sm:flex-wrap sm:overflow-visible sm:mx-0 sm:px-0
                   [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
      >
        {followups.map((q, i) => (
          <button
            key={`fu-${turnIndex}-${i}`}
            type="button"
            data-testid={`ai-followup-${turnIndex}-${i}`}
            disabled={disabled}
            onClick={() => onPick(q)}
            className="shrink-0 whitespace-nowrap sm:shrink sm:whitespace-normal text-left px-3 py-1.5 rounded-full bg-emerald-500/5 border border-emerald-500/30 text-xs text-emerald-200/90 hover:border-emerald-400/60 hover:bg-emerald-500/10 hover:text-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
}

function AdminDebugWidget({ messages, modelName }) {
  // Pull admin lifetime summary lazily — only when the section
  // actually mounts for an admin user.
  const { data: weekly } = useQuery({
    queryKey: ["ai-usage-summary", 7],
    queryFn: () => fetchAdminUsageSummary(7),
    staleTime: 60_000,
  });

  const sessionTotals = useMemo(() => {
    let turns = 0, input = 0, output = 0, cost = 0;
    for (const t of messages) {
      if (t.role === "assistant" && t.usage) {
        turns += 1;
        input += t.usage.input_tokens || 0;
        output += t.usage.output_tokens || 0;
        cost += t.usage.estimated_cost_usd || 0;
      }
    }
    return { turns, input, output, cost: Math.round(cost * 100000) / 100000 };
  }, [messages]);

  return (
    <div
      data-testid="ai-admin-debug"
      className="mt-4 border-t border-zinc-800/80 pt-3 text-[11px] text-zinc-500 flex flex-wrap gap-x-4 gap-y-1 tabular-nums"
    >
      <span>
        {COPY.adminSessionLabel}{" "}
        <span className="text-zinc-300">
          {sessionTotals.turns} Turns · {deAbbrev(sessionTotals.input)} in /{" "}
          {deAbbrev(sessionTotals.output)} out ·{" "}
          {fmtCostUsd(sessionTotals.cost)}
        </span>
        {modelName ? <span className="text-zinc-600"> · {modelName}</span> : null}
      </span>
      {weekly?.totals ? (
        <span>
          {COPY.adminWeeklyLabel}{" "}
          <span className="text-zinc-300">
            {weekly.totals.turns} Turns · {fmtCostUsd(weekly.totals.estimated_cost_usd)}
          </span>
        </span>
      ) : null}
    </div>
  );
}

/* ---------------- Main component ---------------- */
// Welle 5 Phase 3 — `fullscreen` renders the mobile modal variant:
// fixed header (with close), only the conversation scrolls, composer
// pinned at the bottom with iOS safe-area padding.
export default function AIAdvisorPanel({
  listingUlid,
  vertical,
  country = null,
  openSignal,
  fullscreen = false,
  onRequestClose,
  card = null,
  // Klagenfurt P0.4d Punkt 1 — return-state from a comparable click.
  // Payload: { chatScrollTop, anchorTurnUlid, anchorTurnIndex,
  //            conversationId, requestId }. `requestId` bumps each
  // time ListingDetailPage forwards a new snapshot so the panel's
  // restore effect re-runs even if the object identity is stable.
  restoreSignal = null,
  // Called AFTER a restore attempt actually landed (the topmost
  // visible turn matches the anchor OR the raw scrollTop was
  // applied). Only then is it safe for the caller to clear the
  // sessionStorage snapshot (P0.4d cleanup contract).
  onRestoreComplete = null,
  // Klagenfurt P0.4d — the fullscreen mobile variant is portal-
  // mounted with `display:none` on desktop viewports; without this
  // gate its snapshotter would race the desktop panel's registration
  // for the SAME listingUlid and win by mount-order, returning
  // scrollTop=0 to the widget click. Callers keep the default `true`
  // for the always-visible desktop side panel and pass `open`-gated
  // truth for the FAB variant.
  snapshotterActive = true,
}) {
  const { user, isPro } = useAuth();
  const isAdmin = user?.role === "admin";

  // Phase E — non-pro users cannot send. We render a locked variant
  // of the panel: chips & composer disabled, a paywall card sitting
  // where the empty state would otherwise be. Tapping the "KI fragen"
  // button on a DealCard already opens the modal — this section is
  // the explainer + secondary CTA once they're on the detail page.
  const [paywallModalOpen, setPaywallModalOpen] = useState(false);

  // The vertical the backend expects: card.vertical is either "auto"
  // or "immo"; the quick-actions catalog accepts both names directly.
  const verticalParam = vertical === "auto" ? "auto" : "immo";

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [resetPending, setResetPending] = useState(false);
  const [errorState, setErrorState] = useState(null);
  const [retryAfterSec, setRetryAfterSec] = useState(0);
  const [conversationId, setConversationId] = useState(null);
  const [modelName, setModelName] = useState(null);
  const [quota, setQuota] = useState(null);
  const [showAccent, setShowAccent] = useState(false);

  const sectionRef = useRef(null);
  const textareaRef = useRef(null);

  /* Quick-action catalog. Cached aggressively. */
  const { data: qaData, isLoading: qaLoading } = useQuery({
    queryKey: ["ai-quick-actions", verticalParam, country || null],
    queryFn: () => fetchQuickActions(verticalParam, country),
    staleTime: 60 * 60 * 1000,
  });

  /* Existing conversation on mount (read-only). */
  const { data: existingConv } = useQuery({
    queryKey: ["ai-conv-by-listing", listingUlid],
    queryFn: () => fetchConversationByListing(listingUlid),
    enabled: Boolean(listingUlid),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (existingConv && existingConv.turns) {
      setMessages(existingConv.turns);
      setConversationId(existingConv.ulid);
      setModelName(existingConv.model_name);
    }
  }, [existingConv]);

  /* Deep-link from DealCard (`?ai=open`): scroll + glow once. */
  useEffect(() => {
    if (!openSignal || fullscreen) return;
    if (sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setShowAccent(true);
    const t = setTimeout(() => setShowAccent(false), 1500);
    return () => clearTimeout(t);
  }, [openSignal, fullscreen]);

  /* Retry-after countdown ticker. */
  useEffect(() => {
    if (retryAfterSec <= 0) return;
    const id = setTimeout(() => setRetryAfterSec((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(id);
  }, [retryAfterSec]);

  /* Auto-resize textarea up to ~4 rows. */
  const autoSizeTextarea = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, []);
  useEffect(() => {
    autoSizeTextarea();
  }, [input, autoSizeTextarea]);

  /* Auto-scroll (UX micro-polish).
     - Only active after the user has interacted in THIS session (loading an
       existing conversation on mount must never move the host page).
     - New USER turn → bring that message to the TOP of the conversation
       container so the incoming AI answer renders below it and is read from
       its start (instead of jumping past it to the very bottom).
     - New ASSISTANT turn → do NOT force-jump to the bottom. Only follow to
       the bottom if the user is already near the bottom (actively watching
       the live response).
     - Never uses scrollIntoView (it bubbles and scrolls the host page); we
       manipulate the container's own scrollTop only. */
  const userInteractedRef = useRef(false);
  const conversationRef = useRef(null);
  const bottomSpacerRef = useRef(null);
  const prevCountRef = useRef(0);
  // Default false: on a fresh session no scroll event has fired yet, so the
  // first assistant turn must NOT auto-jump to the bottom.
  const nearBottomRef = useRef(false);

  const handleConversationScroll = useCallback((e) => {
    const c = e.currentTarget;
    nearBottomRef.current =
      c.scrollHeight - c.scrollTop - c.clientHeight <= 120;
  }, []);

  useEffect(() => {
    const prevCount = prevCountRef.current;
    prevCountRef.current = messages.length;
    if (!userInteractedRef.current) return;
    const container = conversationRef.current;
    if (!container) return;
    if (messages.length <= prevCount) return; // reset / no append

    const last = messages[messages.length - 1];
    // We pin the NEWEST user turn to the container top both when it is sent
    // AND when the following assistant reply lands (unless the user is
    // actively watching from the bottom) — so the answer is always read from
    // its beginning instead of being scrolled past.
    const pinUserToTop =
      last?.role === "user" ||
      (last?.role === "assistant" && !nearBottomRef.current);

    requestAnimationFrame(() => {
      const spacer = bottomSpacerRef.current;
      if (pinUserToTop) {
        const nodes = container.querySelectorAll('[data-testid="ai-turn-user"]');
        const anchor = nodes[nodes.length - 1];
        if (!anchor) {
          if (spacer) spacer.style.height = "0px";
          return;
        }
        // Reset the spacer, then grow it just enough that the newest user
        // turn CAN be scrolled to the top (the browser otherwise clamps
        // scrollTop to scrollHeight-clientHeight because the user turn is the
        // last element with no content below it).
        if (spacer) spacer.style.height = "0px";
        const cRect = container.getBoundingClientRect();
        const anchorScrollTop =
          anchor.getBoundingClientRect().top - cRect.top + container.scrollTop;
        const belowAnchor = container.scrollHeight - anchorScrollTop;
        const needed = Math.max(0, container.clientHeight - belowAnchor);
        if (spacer) spacer.style.height = `${needed}px`;
        container.scrollTop = Math.max(0, anchorScrollTop - 8);
        if (last?.role === "user") nearBottomRef.current = false;
      } else if (last?.role === "assistant" && nearBottomRef.current) {
        // User is watching from the bottom → follow the live response.
        if (spacer) spacer.style.height = "0px";
        container.scrollTop = container.scrollHeight;
      }
    });
  }, [messages]);

  /* Klagenfurt P0.4d Punkt 1 — expose a snapshotter so the
     DealComparables chat widget can capture the EXACT current chat
     scroll offset + the topmost visible turn's stable id at click
     time. The widget writes them into `advisorReturnStore`; on BACK
     the listing page hands the snapshot into `restoreSignal`.

     Multi-panel resilience: on desktop, the AIAdvisorMobileFab
     portal-mounts a hidden fullscreen AIAdvisorPanel alongside the
     visible sidebar panel. Both register a snapshotter under the
     SAME listingUlid. The registry is multi-entry (Set), and each
     snapshotter self-gates on visibility (`offsetParent === null`
     or `clientHeight === 0` → return null) so the visible panel
     always wins. `snapshotterActive` adds a belt-and-suspenders
     opt-out for callers that already know they are inactive (FAB
     closed) so we don't even attempt a DOM read. */
  useEffect(() => {
    if (!listingUlid) return undefined;
    if (!snapshotterActive) return undefined;
    const snapshotter = () => {
      const container = conversationRef.current;
      if (!container) return null;
      // Visibility gate. offsetParent === null is triggered by any
      // ancestor with `display:none`. clientHeight === 0 catches
      // collapsed/zero-height layouts. Either way the panel cannot
      // meaningfully snapshot a chat offset.
      if (container.offsetParent === null) return null;
      if (container.clientHeight === 0) return null;
      const chatScrollTop = container.scrollTop || 0;
      // Determine the topmost visible turn — anchor of choice for
      // restore. Uses the container's top edge as the reference line.
      let anchorTurnUlid = null;
      let anchorTurnIndex = null;
      try {
        const nodes = container.querySelectorAll("[data-turn-index]");
        const cTop = container.getBoundingClientRect().top;
        for (const node of nodes) {
          const rect = node.getBoundingClientRect();
          if (rect.bottom >= cTop) {
            anchorTurnUlid = node.getAttribute("data-turn-ulid") || null;
            const idxAttr = node.getAttribute("data-turn-index");
            const parsed = idxAttr ? Number(idxAttr) : NaN;
            anchorTurnIndex = Number.isFinite(parsed) ? parsed : null;
            break;
          }
        }
      } catch {
        /* defensive — a snapshot failure must NEVER break the click. */
      }
      return {
        chatScrollTop,
        anchorTurnUlid,
        anchorTurnIndex,
        conversationId: conversationId || null,
      };
    };
    return registerAdvisorSnapshotter(listingUlid, snapshotter);
  }, [listingUlid, conversationId, snapshotterActive]);

  /* Klagenfurt P0.4d Punkt 1 — restore the exact chat scroll on BACK.
     Runs after the panel has hydrated its `messages` array from the
     server-persisted conversation. Preference order:
       1. anchorTurnUlid  — stable id (P0.4d turns)
       2. anchorTurnIndex — array-position fallback (legacy turns)
       3. chatScrollTop   — raw offset fallback
     A failed lookup leaves the container untouched AND does NOT call
     onRestoreComplete → the caller MUST NOT clear the sessionStorage
     snapshot. This is the "state erst nach erfolgreichem Restore
     löschen" contract requested by the user for P0.4d.

     Additional guard: the restore only fires when the SAME
     conversation that captured the snapshot is now loaded. This
     prevents a stale snapshot from targeting a foreign conversation
     the panel switched to during navigation. */
  const restoreAppliedRef = useRef(null);
  useEffect(() => {
    if (!restoreSignal) {
      restoreAppliedRef.current = null;
      return;
    }
    if (restoreAppliedRef.current === restoreSignal.requestId) return;
    if (!messages || messages.length === 0) return;
    // Same-conversation guard. When the caller has no
    // `conversationId` in the snapshot (e.g. modal-path or older
    // writers) we relax the check — the widget-path always writes it.
    if (
      restoreSignal.conversationId
      && conversationId
      && restoreSignal.conversationId !== conversationId
    ) {
      // Different conversation was loaded — the anchor can't be
      // trusted. Do NOT clear the sessionStorage snapshot; the user
      // may still expect a retry when the correct conversation
      // hydrates.
      return;
    }
    const container = conversationRef.current;
    if (!container) return;
    // Give the DOM one paint so freshly-hydrated turns have real
    // bounding rects.
    requestAnimationFrame(() => {
      const c = conversationRef.current;
      if (!c) return;
      const doLand = () => {
        restoreAppliedRef.current = restoreSignal.requestId;
        // Suppress the auto-scroll effect for this batch — the new
        // scrollTop already reflects the user's intent, and we
        // don't want a "pin last user turn" jump to override it.
        userInteractedRef.current = false;
        prevCountRef.current = messages.length;
        nearBottomRef.current =
          c.scrollHeight - c.scrollTop - c.clientHeight <= 120;
        if (typeof onRestoreComplete === "function") {
          onRestoreComplete();
        }
      };
      // Preference: EXACT raw chatScrollTop (source of truth — the
      // user's actual pixel position at click time). Anchors are
      // only a semantic hint used when the raw offset is unusable
      // (missing, or the container has re-flowed and the offset now
      // points past scrollHeight).
      const rawTop = restoreSignal.chatScrollTop;
      const maxTop = Math.max(0, c.scrollHeight - c.clientHeight);
      if (typeof rawTop === "number" && rawTop >= 0 && rawTop <= maxTop + 4) {
        c.scrollTop = Math.min(rawTop, maxTop);
        doLand();
        return;
      }
      // Raw offset unavailable OR exceeds scrollHeight (container
      // shrank) → try anchor-based landing so we at least land on
      // the right turn.
      const ulidToFind = restoreSignal.anchorTurnUlid;
      if (ulidToFind) {
        const node = c.querySelector(
          `[data-turn-ulid="${CSS.escape(ulidToFind)}"]`,
        );
        if (node) {
          const cRect = c.getBoundingClientRect();
          const nRect = node.getBoundingClientRect();
          c.scrollTop = Math.max(0, nRect.top - cRect.top + c.scrollTop - 8);
          doLand();
          return;
        }
      }
      const idx = restoreSignal.anchorTurnIndex;
      if (
        typeof idx === "number" &&
        idx >= 0 &&
        idx < messages.length
      ) {
        const nodes = c.querySelectorAll("[data-turn-index]");
        for (const node of nodes) {
          if (
            Number(node.getAttribute("data-turn-index")) === idx
          ) {
            const cRect = c.getBoundingClientRect();
            const nRect = node.getBoundingClientRect();
            c.scrollTop = Math.max(0, nRect.top - cRect.top + c.scrollTop - 8);
            doLand();
            return;
          }
        }
      }
      // Absolute last resort — raw offset even if it exceeds the
      // container (clamps to maxTop). Better than no restore at all.
      if (typeof rawTop === "number" && rawTop >= 0) {
        c.scrollTop = Math.min(rawTop, maxTop);
        doLand();
        return;
      }
      // No usable anchor and no raw offset → DO NOT call
      // onRestoreComplete. The caller must NOT clear the
      // sessionStorage snapshot; a later layout pass may still
      // allow the restore to land.
    });
  }, [restoreSignal, messages, onRestoreComplete, conversationId]);

  /* The one mutation that talks to the advisor. */
  const pendingRequestIdsRef = useRef(new Map());
  const streamAbortRef = useRef(null);
  const streamPlaceholderIndexRef = useRef(-1);
  const sendMut = useMutation({
    mutationFn: async ({ message, quickActionSlug }) => {
      // Idempotency: stable client_request_id per send attempt — a retry
      // of the same message reuses the ID so the backend dedupes.
      const requestKey = `${listingUlid}|${quickActionSlug || message || ""}`;
      let clientRequestId = pendingRequestIdsRef.current.get(requestKey);
      if (!clientRequestId) {
        clientRequestId = `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
        pendingRequestIdsRef.current.set(requestKey, clientRequestId);
      }
      // P1.6 — Try SSE streaming first. On 404 (feature flag off) or
      // any error BEFORE the first delta arrives → fall back to the
      // non-streaming endpoint. AFTER the first delta we NEVER
      // retry — the client_request_id lock keeps the server consistent
      // and a retry would double-bill Claude.
      const controller = new AbortController();
      streamAbortRef.current = controller;
      let deltaSeen = false;
      let completedResponse = null;
      try {
        const result = await streamAdvisorTurn({
          listingUlid,
          message,
          quickActionSlug,
          resetConversation: resetPending,
          clientRequestId,
          signal: controller.signal,
          onEvent: (ev) => {
            if (ev.kind === "start") {
              // Insert an empty assistant placeholder to receive deltas.
              setMessages((prev) => {
                streamPlaceholderIndexRef.current = prev.length;
                return [
                  ...prev,
                  {
                    role: "assistant",
                    content: "",
                    _streaming: true,
                    quick_action_slug: null,
                    usage: null,
                    suggested_followups: [],
                    widgets: [],
                    created_at: new Date().toISOString(),
                  },
                ];
              });
            } else if (ev.kind === "delta" && typeof ev.text === "string") {
              deltaSeen = true;
              setMessages((prev) => {
                const idx = streamPlaceholderIndexRef.current;
                if (idx < 0 || idx >= prev.length) return prev;
                const next = prev.slice();
                next[idx] = {
                  ...next[idx],
                  content: (next[idx].content || "") + ev.text,
                };
                return next;
              });
            } else if (ev.kind === "complete" && ev.response) {
              completedResponse = ev.response;
            }
            // ping / error handled by outer try/catch (error) or
            // ignored (ping is just a heartbeat).
          },
        });
        if (result && result.kind === "error") {
          const err = new Error(result.message || "stream error");
          err.streamError = true;
          throw err;
        }
        pendingRequestIdsRef.current.delete(requestKey);
        return completedResponse;
      } catch (err) {
        // P0 2026-06 — ALWAYS discard the streaming placeholder on error
        // (even mid-stream after deltas): the backend never persisted the
        // partial answer and releases the idempotency lock, so a manual
        // retry re-runs the turn. Leaving the partial bubble behind
        // produced phantom half-answers stacked above the retry.
        setMessages((prev) => {
          const idx = streamPlaceholderIndexRef.current;
          if (idx < 0 || idx >= prev.length || !prev[idx]?._streaming) return prev;
          return prev.slice(0, idx).concat(prev.slice(idx + 1));
        });
        streamPlaceholderIndexRef.current = -1;
        // P0.1 — HARDENED fallback contract:
        //  * `status === 404`   → streaming globally disabled (feature
        //    flag) → fall back to the legacy endpoint.
        //  * `streamingUnavailable` → the runtime doesn't ship the
        //    fetch API (no request was ever issued) → fall back.
        // Every other error is surfaced. In particular:
        //  * 401 that survived the single-flight refresh,
        //  * 402 / 403 / 429 / 500,
        //  * `TypeError: Network request failed` / timeout / abort /
        //    mid-stream disconnect — the request may already have
        //    reached Claude; a fallback would double-bill and eat
        //    another slot of the 30/hour rate limit. The UI keeps
        //    the `client_request_id` on the pending map so a manual
        //    retry is idempotent.
        const status = err?.status;
        const canFallback = status === 404 || err?.streamingUnavailable === true;
        if (canFallback) {
          const data = await sendAdvisorTurn({
            listingUlid,
            message,
            quickActionSlug,
            resetConversation: resetPending,
            clientRequestId,
          });
          pendingRequestIdsRef.current.delete(requestKey);
          return data;
        }
        // NOTE: we deliberately keep the client_request_id in the
        // pending map so the user's next manual retry reuses it and
        // the backend dedupe kicks in.
        throw err;
      } finally {
        streamAbortRef.current = null;
      }
    },
    onMutate: ({ message, quickActionSlug }) => {
      // Mark this session as user-interacted so the auto-scroll-to-bottom
      // effect is allowed to fire from now on.
      userInteractedRef.current = true;
      // Optimistically render the user turn so the chat feels live
      // even before Claude responds.
      const userContent =
        quickActionSlug
          ? qaLabel(qaData?.items?.find((it) => it.slug === quickActionSlug)) ||
            `(${quickActionSlug})`
          : message;
      setMessages((prev) => [
        ...prev,
        {
          role: "user",
          content: userContent,
          quick_action_slug: quickActionSlug || null,
          usage: null,
          created_at: new Date().toISOString(),
        },
      ]);
      setErrorState(null);
    },
    onSuccess: (data) => {
      setConversationId(data.conversation_id);
      setModelName(data.model_name);
      setResetPending(false);
      if (data.ai_quota) setQuota(data.ai_quota);
      // P1.6 — if the streaming path staged a live placeholder, replace
      // it with the canonical assistant turn (real widgets, follow-ups,
      // final usage). If no placeholder exists (fallback path), append.
      setMessages((prev) => {
        const idx = streamPlaceholderIndexRef.current;
        const canonical = {
          role: "assistant",
          content: data.assistant_message,
          quick_action_slug: null,
          usage: data.usage || null,
          // UX Final Polish — P5: stash the LLM's inline follow-up
          // suggestions on the turn itself so they render right below
          // the bubble. Empty array → no chips section, no UI noise.
          suggested_followups: Array.isArray(data.suggested_followups)
            ? data.suggested_followups
            : [],
          widgets: Array.isArray(data.widgets) ? data.widgets : [],
          created_at: new Date().toISOString(),
        };
        if (idx >= 0 && idx < prev.length && prev[idx]?._streaming) {
          const next = prev.slice();
          next[idx] = canonical;
          streamPlaceholderIndexRef.current = -1;
          return next;
        }
        return [...prev, canonical];
      });
    },
    onError: async (err) => {
      const status = err?.response?.status;
      const data = err?.response?.data;

      // P0 hotfix — 409 request_in_progress: a transparent browser/proxy
      // retry hit the server while the first attempt is still producing
      // the answer. Do NOT roll back the user turn — the answer WILL be
      // persisted on the conversation: poll it in.
      if (status === 409 && data?.error?.code === "request_in_progress") {
        setRescuing(true);
        try {
          for (const delay of [3000, 5000, 8000, 10000, 12000, 15000, 15000]) {
            await new Promise((r) => setTimeout(r, delay));
            try {
              const conv = await fetchConversationByListing(listingUlid);
              const turns = conv?.turns || [];
              if (
                turns.length > messagesLenRef.current &&
                turns[turns.length - 1]?.role === "assistant"
              ) {
                setMessages(
                  turns.map((turn) => ({
                    role: turn.role,
                    content: turn.content,
                    widgets: Array.isArray(turn.widgets) ? turn.widgets : [],
                    created_at: turn.created_at,
                  })),
                );
                setErrorState(null);
                return;
              }
            } catch {
              /* keep polling */
            }
          }
        } finally {
          setRescuing(false);
        }
        setErrorState({
          kind: "generic",
          message: _t("assistant.stillWorkingReload"),
        });
        return;
      }

      // Roll back the optimistic user turn so the chat isn't left in
      // a half-state.
      setMessages((prev) => {
        if (prev.length === 0) return prev;
        const last = prev[prev.length - 1];
        if (last.role === "user") return prev.slice(0, -1);
        return prev;
      });

      if (status === 429) {
        const retryAfterHeader =
          err?.response?.headers?.["retry-after"] ||
          err?.response?.headers?.["Retry-After"];
        const detailRetry = data?.error?.details?.retry_after;
        const seconds = Number(retryAfterHeader) || Number(detailRetry) || 60;
        setRetryAfterSec(seconds);
        setErrorState({
          kind: "rate_limit",
          message:
            data?.error?.message ||
            _t("rateLimitHour"),
        });
      } else if (status === 422) {
        setErrorState({ kind: "validation", message: formatApiError(err) });
      } else {
        // Advisor P0 — surface the server's request_id so support can
        // find the exact failed turn in the logs.
        setErrorState({
          kind: "generic",
          message: formatApiError(err),
          requestId: data?.error?.details?.request_id || null,
        });
      }
    },
  });

  const [rescuing, setRescuing] = useState(false);
  const messagesLenRef = useRef(0);
  messagesLenRef.current = messages.length;
  const isBusy = sendMut.isPending || rescuing;
  // Sprint J P9 — retry-able last send. We stash the last attempted
  // payload so the "Erneut senden" button in the error band can
  // actually re-fire the request (instead of just clearing the error,
  // which left the user stuck with their message gone).
  const lastSendRef = useRef(null);

  const actuallySend = useCallback(
    (payload) => {
      lastSendRef.current = payload;
      sendMut.mutate(payload);
    },
    [sendMut],
  );

  const handleRetryLast = useCallback(() => {
    setErrorState(null);
    const last = lastSendRef.current;
    if (last) {
      sendMut.mutate(last);
    }
  }, [sendMut]);

  const handleSend = useCallback(() => {
    if (!isPro) {
      setPaywallModalOpen(true);
      return;
    }
    const text = input.trim();
    if (!text || isBusy) return;
    setInput("");
    actuallySend({ message: text, quickActionSlug: null });
  }, [input, isBusy, isPro, actuallySend]);

  const handleQuickAction = useCallback(
    (slug) => {
      if (!isPro) {
        setPaywallModalOpen(true);
        return;
      }
      if (isBusy) return;
      actuallySend({ message: null, quickActionSlug: slug });
    },
    [isBusy, isPro, actuallySend],
  );

  const handleReset = useCallback(() => {
    if (messages.length === 0) return;
    if (!window.confirm(COPY.resetConfirm)) return;
    setResetPending(true);
    setMessages([]);
    setConversationId(null);
  }, [messages.length]);

  // UX Final Polish — P5 follow-up chip click. Sends the suggested
  // question immediately as the next user turn. `isBusy` guard
  // mirrors `handleSend` so we can't fire two LLM calls in flight.
  const handlePickFollowup = useCallback(
    (questionText) => {
      if (!isPro) {
        setPaywallModalOpen(true);
        return;
      }
      const text = (questionText || "").trim();
      if (!text || isBusy) return;
      actuallySend({ message: text, quickActionSlug: null });
    },
    [isPro, isBusy, actuallySend],
  );

  // UX Final Polish — P6 speech-to-text. Uses the Web Speech API
  // (de-DE). Falls back to "hidden mic button" when the browser
  // doesn't expose `SpeechRecognition` (e.g. some embedded webviews).
  const speech = useSpeechRecognition({
    locale: "de-DE",
    onTranscript: (text) => {
      if (!text) return;
      setInput((prev) => (prev ? prev.trim() + " " + text : text));
    },
  });

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const empty = messages.length === 0 && !isBusy;

  return (
    <section
      ref={sectionRef}
      data-testid="ai-advisor-panel"
      data-vertical={verticalParam}
      className={
        fullscreen
          ? "flex h-full min-h-0 flex-col bg-zinc-950"
          : `border bg-zinc-950/60 rounded-2xl p-5 space-y-4 transition-shadow lg:flex lg:flex-col lg:grow lg:min-h-0 lg:overflow-hidden ${
              showAccent
                ? "border-emerald-400/60 shadow-[0_0_0_4px_rgba(16,185,129,0.18)]"
                : "border-zinc-800"
            }`
      }
    >
      {/* Sprint F Co-Pilot — Header with avatar + Pro pill makes
          the AI feel like a branded participant, not a chat widget. */}
      {/* Master-Sprint P1 — compact mobile header: in fullscreen the
          header is ONE slim row (small avatar, sm title, no tagline)
          so the conversation owns the viewport. */}
      <div
        className={
          fullscreen
            ? "shrink-0 flex items-center justify-between gap-2.5 border-b border-zinc-800/80 px-3 pb-2"
            : "flex items-start justify-between gap-3 flex-wrap lg:shrink-0"
        }
        style={
          fullscreen
            ? { paddingTop: "calc(env(safe-area-inset-top, 0px) + 8px)" }
            : undefined
        }
      >
        <div className={`flex min-w-0 ${fullscreen ? "items-center gap-2" : "items-start gap-3"}`}>
          <div
            aria-hidden="true"
            className={`shrink-0 rounded-full
                       bg-gradient-to-br from-emerald-400 to-teal-500
                       grid place-items-center
                       shadow-[0_0_0_3px_rgba(16,185,129,0.15)] ${
                         fullscreen ? "w-7 h-7" : "w-10 h-10"
                       }`}
          >
            <span className={`text-zinc-950 font-bold leading-none ${fullscreen ? "text-sm" : "text-lg"}`}>✦</span>
          </div>
          <div className="space-y-0.5 min-w-0">
            <h2
              data-testid="ai-section-title"
              className={`font-medium text-zinc-100 flex items-center gap-2 ${
                fullscreen ? "text-sm flex-nowrap truncate" : "text-lg flex-wrap"
              }`}
            >
              <span className={fullscreen ? "truncate" : undefined}>{COPY.sectionTitle}</span>
              <span
                data-testid="ai-pro-pill"
                className="shrink-0 text-[9px] tracking-[0.12em] px-1.5 py-0.5 rounded-full
                           bg-emerald-500/15 border border-emerald-400/40
                           text-emerald-300 uppercase font-semibold"
              >
                Pro
              </span>
            </h2>
            {!fullscreen && (
              <div className="text-[11px] text-zinc-500 tracking-wide truncate">
                {COPY.poweredBy} · {_t("liveTagline")}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* V4.2 — bridge to the general market chat (one AI, two modes) */}
          <a
            href="/ai"
            data-testid="ai-market-chat-bridge"
            className="hidden sm:inline text-xs text-zinc-500 hover:text-amber-300 underline-offset-4 hover:underline"
            title={_t("marketChatBridgeTitle", { defaultValue: "Allgemeine Marktfrage? Zum KI-Marktberater" })}
          >
            {_t("marketChatBridge", { defaultValue: "Marktfrage? → KI-Tab" })}
          </a>
          {messages.length > 0 && (
            <button
              type="button"
              data-testid="ai-reset-conversation"
              onClick={handleReset}
              className="text-xs text-zinc-400 hover:text-emerald-300 underline-offset-4 hover:underline"
            >
              {COPY.resetLink}
            </button>
          )}
          {fullscreen && listingUlid && (
            <ShareDealButton listingUlid={listingUlid} variant="icon" />
          )}
          {fullscreen && onRequestClose && (
            <button
              type="button"
              data-testid="ai-fullscreen-close"
              onClick={onRequestClose}
              aria-label={i18n.t("common:ui.close")}
              className="h-9 w-9 rounded-full bg-zinc-900/80 border border-zinc-700
                         text-zinc-300 hover:text-zinc-100
                         flex items-center justify-center"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Conversation / empty state.
          P0 2026-06 layout fix — DealPreviewInsight + QuickActionChips
          moved INSIDE this scroll container (see below): on small
          laptop heights the fixed-size blocks overflowed the panel and
          overlapped the Finanzierung/Merken/Teilen buttons below the
          aside. Now only header + composer are fixed; everything else
          scrolls, so the panel NEVER overflows its rounded border. */}
      <div
        data-testid="ai-conversation"
        ref={conversationRef}
        onScroll={handleConversationScroll}
        className={`space-y-3 overflow-y-auto bg-zinc-950 rounded-xl ${
          fullscreen
            ? "grow min-h-0 px-4 py-3"
            : "max-h-[min(720px,calc(100vh-16rem))] lg:max-h-none lg:grow lg:min-h-0 pr-1"
        }
                   [&::-webkit-scrollbar]:w-1.5
                   [&::-webkit-scrollbar-track]:bg-zinc-950
                   [&::-webkit-scrollbar-thumb]:bg-zinc-700
                   [&::-webkit-scrollbar-thumb]:rounded-full
                   [&::-webkit-scrollbar-thumb:hover]:bg-zinc-600
                   [scrollbar-width:thin] [scrollbar-color:#3f3f46_#09090b]`}
      >
        {/* P1.1 — Deal-specific opening insight (empty state only).
            Uses ALREADY-COMPUTED backend data — NO LLM call on open. */}
        {messages.length === 0 && isPro && (
          <div className="animate-in fade-in duration-300">
            <DealPreviewInsight card={card} vertical={verticalParam} />
          </div>
        )}
        {/* Quick-action chips — conversation-first: only shown before
            the first turn; collapse away once a message exists. */}
        {messages.length === 0 && (
          <div className="animate-in fade-in duration-300">
            <QuickActionChips
              items={qaData?.items}
              isLoading={qaLoading}
              disabled={isBusy || retryAfterSec > 0}
              onPick={handleQuickAction}
            />
          </div>
        )}
        {empty && !isPro && (
          <div
            data-testid="ai-empty-state-locked"
            className="text-sm border border-emerald-500/30 bg-emerald-500/10 rounded-2xl px-4 py-5 text-center space-y-3"
          >
            <div className="text-emerald-200 text-base">
              KI-Berater ist mit DealAlert Pro freigeschaltet.
            </div>
            <div className="text-zinc-400 text-xs">
              Voller Risiko-Check, Rendite-Schätzungen und Verhandlungstipps –
              10 €/Monat, jederzeit kündbar.
            </div>
            <div className="pt-1">
              <ProUpgradeButton data-testid="ai-panel-upgrade-button">
                Pro freischalten
              </ProUpgradeButton>
            </div>
          </div>
        )}
        {empty && isPro && (
          <div
            data-testid="ai-empty-state"
            className="text-sm text-zinc-400 border border-dashed border-zinc-800 rounded-2xl px-4 py-5 text-center space-y-1"
          >
            <div className="text-zinc-200">{COPY.emptyHeadline}</div>
            <div className="text-zinc-500 text-xs">↑ {COPY.emptySubline}</div>
          </div>
        )}
        {messages.map((t, i) => {
          const isLastAssistant =
            t.role === "assistant" && i === messages.length - 1;
          // Klagenfurt P0.4d Punkt 1 — every turn ships as a
          // scrollable anchor with a stable `data-turn-ulid` (server
          // ULID) + `data-turn-index` (positional fallback). The
          // widget uses these when snapshotting the current chat
          // scroll offset; the restore-effect scrolls to them on BACK.
          const turnUlid = t.turn_ulid || "";
          return (
            <div
              key={`${t.role}-${i}-${t.created_at || i}`}
              data-turn-index={i}
              data-turn-ulid={turnUlid}
              data-turn-role={t.role}
              className="min-w-0"
            >
              <MessageBubble turn={t} />
              {t.role === "assistant" &&
                Array.isArray(t.widgets) &&
                t.widgets.length > 0 && (
                  <ChatWidgets
                    widgets={t.widgets}
                    variant="advisor"
                    conversationId={conversationId}
                  />
                )}
              {/* P5 — render follow-up chips only beneath the LATEST
                  assistant reply to keep the chat-history clean. Older
                  turns drop their chips when a newer turn arrives. */}
              {isLastAssistant && t.suggested_followups?.length > 0 && (
                <FollowupChips
                  followups={t.suggested_followups}
                  disabled={isBusy || retryAfterSec > 0}
                  onPick={handlePickFollowup}
                  turnIndex={i}
                />
              )}
            </div>
          );
        })}
        {isBusy && <ThinkingBubble />}
        {/* Dynamic bottom spacer — grown just enough (see auto-scroll
            effect) so the newest user turn can be pinned to the container
            top and the AI answer is read from its beginning. */}
        <div ref={bottomSpacerRef} aria-hidden="true" style={{ height: 0 }} />
      </div>

      {/* Error band */}
      {errorState && (
        <div
          data-testid="ai-error-band"
          className={`text-xs px-3 py-2 rounded-lg border ${
            fullscreen ? "shrink-0 mx-4 mb-2" : "lg:shrink-0"
          } ${
            errorState.kind === "rate_limit"
              ? "bg-amber-500/10 border-amber-500/40 text-amber-200"
              : "bg-rose-500/10 border-rose-500/40 text-rose-200"
          }`}
        >
          {errorState.kind === "rate_limit" ? (
            <>
              <span className="font-medium">{COPY.rateLimitedPrefix}</span>{" "}
              {errorState.message}
              {retryAfterSec > 0 && (
                <span className="ml-1 text-amber-300">
                  {COPY.retryIn(retryAfterSec)}
                </span>
              )}
            </>
          ) : (
            <>
              {errorState.message}
              {errorState.kind === "generic" && (
                <button
                  type="button"
                  data-testid="ai-error-retry"
                  onClick={handleRetryLast}
                  className="ml-2 underline underline-offset-2"
                >
                  {COPY.errorRetry}
                </button>
              )}
              {errorState.requestId && (
                <span
                  data-testid="ai-error-request-id"
                  className="mt-1 block text-[10px] text-rose-300/60"
                >
                  Ref: {errorState.requestId}
                </span>
              )}
            </>
          )}
        </div>
      )}

      {/* V4.7 P1 — trial quota hint: silent until ~5 questions remain */}
      {quota && quota.remaining <= 5 && (
        <div
          data-testid="advisor-quota-hint"
          className="pb-1 text-center text-[10px] text-zinc-500"
        >
          {_t("quota.hint", { count: quota.remaining })}
        </div>
      )}

      {/* Composer
          P6 — mic button toggles Web-Speech-API recording. Hidden when
          the browser doesn't expose SpeechRecognition (older Firefox,
          embedded webviews) so we don't show a button that does
          nothing. Recording state surfaces as a red pulse. */}
      <div
        className={
          fullscreen
            ? "shrink-0 flex items-end gap-2 border-t border-zinc-800/80 bg-zinc-950 px-4 pt-2.5"
            : "flex items-end gap-2 lg:shrink-0"
        }
        style={
          fullscreen
            ? { paddingBottom: "max(env(safe-area-inset-bottom, 0px), 10px)" }
            : undefined
        }
      >
        <textarea
          ref={textareaRef}
          data-testid="ai-composer-input"
          rows={1}
          value={input}
          disabled={isBusy || retryAfterSec > 0}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            fullscreen ? _t("composerPlaceholderShort") : COPY.composerPlaceholder
          }
          className="flex-1 resize-none bg-zinc-900/80 border border-zinc-700 rounded-2xl px-4 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-emerald-400/60 disabled:opacity-60"
          style={{ minHeight: 42, maxHeight: 140 }}
        />
        {speech.isSupported && (
          <button
            type="button"
            data-testid="ai-composer-mic"
            aria-label={speech.isListening ? _t("speech.stop") : _t("speech.start")}
            title={speech.errorMessage || (speech.isListening ? _t("speech.running") : _t("speech.label"))}
            disabled={isBusy || retryAfterSec > 0}
            onClick={speech.toggle}
            className={`h-[42px] w-[42px] flex items-center justify-center rounded-2xl border transition disabled:opacity-50 disabled:cursor-not-allowed ${
              speech.isListening
                ? "bg-rose-500/20 border-rose-400 text-rose-200 animate-pulse"
                : "bg-zinc-900/80 border-zinc-700 text-zinc-300 hover:border-emerald-400/60 hover:text-emerald-200"
            }`}
          >
            {speech.isListening ? <MicOff size={18} /> : <Mic size={18} />}
          </button>
        )}
        <button
          type="button"
          data-testid="ai-composer-send"
          disabled={isBusy || retryAfterSec > 0 || !input.trim()}
          onClick={handleSend}
          className="px-4 h-[42px] rounded-2xl bg-emerald-500 text-zinc-950 text-sm font-medium hover:bg-emerald-400 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {COPY.sendBtn}
        </button>
      </div>

      {isAdmin && (
        <div className={fullscreen ? "shrink-0 px-4 pb-1" : "lg:shrink-0"}>
          <AdminDebugWidget messages={messages} modelName={modelName} />
        </div>
      )}

      {/* Phase E — paywall modal opened by `handleSend` / `handleQuickAction`
          when the current user has no Pro access. Mounted unconditionally
          so the open/close transition is smooth. */}
      <PaywallModal
        open={paywallModalOpen}
        onClose={() => setPaywallModalOpen(false)}
        source="ai_panel"
      />
    </section>
  );
}
